import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { sandataAPI } from "../lib/api";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Upload,
  FileSpreadsheet,
  MapPin,
  Eye,
  CheckCircle,
  XCircle,
  Loader2,
  Download,
} from "lucide-react";

// Required internal fields for exception import
const EXCEPTION_FIELDS = [
  { key: "sandata_exception_id", label: "Exception ID", required: false },
  { key: "sandata_visit_id", label: "Visit ID", required: false },
  { key: "exception_type", label: "Exception Type", required: true },
  { key: "exception_status", label: "Exception Status", required: true },
  { key: "detected_at", label: "Detected Date", required: true },
  { key: "caregiver_sandata_id", label: "Caregiver ID", required: false },
  { key: "caregiver_name", label: "Caregiver Name", required: false },
  { key: "client_sandata_id", label: "Client ID", required: false },
  { key: "client_name", label: "Client Name", required: false },
  { key: "reason", label: "Reason", required: false },
  { key: "notified_at", label: "Notified Date", required: false },
  { key: "verified_at", label: "Verified Date", required: false },
];

const STEPS = [
  { id: 1, title: "Upload", icon: Upload },
  { id: 2, title: "Map Columns", icon: MapPin },
  { id: 3, title: "Preview", icon: Eye },
  { id: 4, title: "Commit", icon: CheckCircle },
];

const SandataImportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Step 2: Column mapping
  const [columnMap, setColumnMap] = useState({});
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  
  // Step 3: Preview
  const [preview, setPreview] = useState(null);
  
  // Step 4: Commit result
  const [commitResult, setCommitResult] = useState(null);

  const canManageImport = ["Admin", "ComplianceManager"].includes(user?.role);

  // Step 1: Handle file upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.toLowerCase();
      if (!ext.endsWith('.csv') && !ext.endsWith('.xlsx') && !ext.endsWith('.xls')) {
        toast.error("Please select a CSV or Excel file");
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }
    
    setLoading(true);
    try {
      const res = await sandataAPI.uploadFile(selectedFile);
      setUploadResult(res.data);
      
      // Auto-detect column mapping
      if (res.data.detected_mapping) {
        setColumnMap(res.data.detected_mapping);
      }
      
      // Load saved presets
      try {
        const presetsRes = await sandataAPI.getColumnMaps("exceptions");
        setSavedPresets(presetsRes.data.column_maps || []);
      } catch (err) {
        console.log("Could not load presets");
      }
      
      toast.success(`File uploaded: ${res.data.row_count} rows detected`);
      setCurrentStep(2);
    } catch (error) {
      const msg = error.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Column mapping
  const handleColumnChange = (internalField, sourceColumn) => {
    setColumnMap(prev => ({
      ...prev,
      [internalField]: sourceColumn === "none" ? undefined : sourceColumn
    }));
  };

  const applyPreset = (preset) => {
    setColumnMap(preset.map_json || {});
    toast.success(`Applied preset: ${preset.name}`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) {
      toast.error("Please enter a preset name");
      return;
    }
    
    try {
      await sandataAPI.createColumnMap({
        file_type: "exceptions",
        name: presetName,
        map_json: columnMap
      });
      toast.success("Preset saved");
      setPresetName("");
      
      // Reload presets
      const presetsRes = await sandataAPI.getColumnMaps("exceptions");
      setSavedPresets(presetsRes.data.column_maps || []);
    } catch (error) {
      toast.error("Failed to save preset");
    }
  };

  const validateMapping = () => {
    const required = EXCEPTION_FIELDS.filter(f => f.required);
    const missing = required.filter(f => !columnMap[f.key]);
    
    // Need either caregiver_id OR caregiver_name
    const hasCaregiverMapping = columnMap.caregiver_sandata_id || columnMap.caregiver_name;
    const hasClientMapping = columnMap.client_sandata_id || columnMap.client_name;
    
    if (missing.length > 0) {
      toast.error(`Missing required mappings: ${missing.map(f => f.label).join(", ")}`);
      return false;
    }
    
    if (!hasCaregiverMapping) {
      toast.error("Must map either Caregiver ID or Caregiver Name");
      return false;
    }
    
    if (!hasClientMapping) {
      toast.error("Must map either Client ID or Client Name");
      return false;
    }
    
    return true;
  };

  // Step 3: Preview
  const handlePreview = async () => {
    if (!validateMapping()) return;
    
    setLoading(true);
    try {
      const res = await sandataAPI.preview(uploadResult.session_id, columnMap);
      setPreview(res.data);
      setCurrentStep(3);
    } catch (error) {
      const msg = error.response?.data?.detail || "Preview failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Commit
  const handleCommit = async () => {
    setLoading(true);
    try {
      const res = await sandataAPI.commit(uploadResult.session_id, columnMap);
      setCommitResult(res.data);
      setCurrentStep(4);
      toast.success(`Import complete: ${res.data.created_count} created, ${res.data.updated_count} updated`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Import failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!canManageImport) {
    return (
      <Layout title="Access Denied">
        <Card>
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-600">Only Admin and Compliance Manager can import Sandata files.</p>
            <Button onClick={() => navigate("/evv-exceptions")} className="mt-4 bg-avida-blue hover:bg-avida-blue-800">
              Back to EVV Inbox
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Sandata Import">
      <div className="space-y-6" data-testid="sandata-import-page">
      {/* Step Indicator */}
      <div className="flex items-center justify-center">
        {STEPS.map((step, idx) => (
          <React.Fragment key={step.id}>
            <div className={`flex flex-col items-center ${currentStep >= step.id ? "text-avida-blue" : "text-slate-400"}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                currentStep > step.id ? "bg-avida-blue border-avida-blue text-white" :
                currentStep === step.id ? "border-avida-blue text-avida-blue" :
                "border-slate-300"
              }`}>
                {currentStep > step.id ? <CheckCircle className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
              </div>
              <span className="text-xs mt-1 font-medium">{step.title}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`w-20 h-0.5 mx-2 ${currentStep > step.id ? "bg-avida-blue" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      {currentStep === 1 && (
        <Card data-testid="step-upload">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Step 1: Upload Sandata Export File
            </CardTitle>
            <CardDescription>
              Upload a CSV or Excel file exported from the Sandata portal. Supported formats: .csv, .xlsx
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <Label htmlFor="file-upload" className="cursor-pointer">
                <span className="text-blue-600 hover:underline">Click to select file</span>
                <span className="text-slate-600"> or drag and drop</span>
              </Label>
              <Input
                id="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {selectedFile && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <span className="font-medium">{selectedFile.name}</span>
                  <Badge variant="outline">{(selectedFile.size / 1024).toFixed(1)} KB</Badge>
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <Button onClick={handleUpload} disabled={!selectedFile || loading} data-testid="upload-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload & Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === 2 && uploadResult && (
        <Card data-testid="step-map-columns">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Step 2: Map Columns
            </CardTitle>
            <CardDescription>
              Map the columns from your file to internal fields. Detected {uploadResult.columns?.length || 0} columns.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Presets */}
            {savedPresets.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-600">Load preset:</span>
                {savedPresets.map(preset => (
                  <Button
                    key={preset.id}
                    variant="outline"
                    size="sm"
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            )}

            {/* Column Mapping Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/3">Internal Field</TableHead>
                    <TableHead className="w-1/3">Source Column</TableHead>
                    <TableHead className="w-1/3">Sample Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {EXCEPTION_FIELDS.map(field => (
                    <TableRow key={field.key}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {field.label}
                          {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={columnMap[field.key] || "none"}
                          onValueChange={(val) => handleColumnChange(field.key, val)}
                        >
                          <SelectTrigger data-testid={`map-${field.key}`}>
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Not Mapped --</SelectItem>
                            {(uploadResult.columns || []).map(col => (
                              <SelectItem key={col} value={col}>{col}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {columnMap[field.key] && uploadResult.sample_data?.[0]?.[columnMap[field.key]]}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Save Preset */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Preset name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                className="w-48"
              />
              <Button variant="outline" onClick={savePreset} disabled={!presetName}>
                Save as Preset
              </Button>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button onClick={handlePreview} disabled={loading} data-testid="preview-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview Import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === 3 && preview && (
        <Card data-testid="step-preview">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Step 3: Preview Import
            </CardTitle>
            <CardDescription>
              Review the import before committing. This cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-slate-900">{preview.exceptions_count || 0}</div>
                  <div className="text-sm text-slate-500">Total Rows</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{preview.new_exceptions || 0}</div>
                  <div className="text-sm text-slate-500">New Records</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{preview.update_exceptions || 0}</div>
                  <div className="text-sm text-slate-500">Updates</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{preview.validation_errors?.length || 0}</div>
                  <div className="text-sm text-slate-500">Validation Errors</div>
                </CardContent>
              </Card>
            </div>

            {/* Warnings */}
            {(preview.unmapped_clients?.length > 0 || preview.unmapped_caregivers?.length > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Unmapped Records</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      The following IDs could not be matched to internal records. They will be imported but may need manual linking.
                    </p>
                    {preview.unmapped_clients?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-amber-800">Clients:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {preview.unmapped_clients.slice(0, 5).map(c => (
                            <Badge key={c.sandata_id} variant="outline" className="bg-white">
                              {c.name || c.sandata_id}
                            </Badge>
                          ))}
                          {preview.unmapped_clients.length > 5 && (
                            <Badge variant="outline" className="bg-white">+{preview.unmapped_clients.length - 5} more</Badge>
                          )}
                        </div>
                      </div>
                    )}
                    {preview.unmapped_caregivers?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-amber-800">Caregivers:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {preview.unmapped_caregivers.slice(0, 5).map(c => (
                            <Badge key={c.sandata_id} variant="outline" className="bg-white">
                              {c.name || c.sandata_id}
                            </Badge>
                          ))}
                          {preview.unmapped_caregivers.length > 5 && (
                            <Badge variant="outline" className="bg-white">+{preview.unmapped_caregivers.length - 5} more</Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {preview.validation_errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 flex items-center gap-2">
                  <XCircle className="h-5 w-5" /> Validation Errors
                </h4>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  {preview.validation_errors.slice(0, 10).map((err, idx) => (
                    <div key={idx} className="text-sm text-red-700">
                      Row {err.row}: {err.field} - {err.error}
                    </div>
                  ))}
                  {preview.validation_errors.length > 10 && (
                    <div className="text-sm text-red-600 font-medium mt-2">
                      +{preview.validation_errors.length - 10} more errors
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sample Data */}
            {preview.sample_data?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Sample Data (first 10 rows)</h4>
                <div className="border rounded-lg overflow-auto max-h-64">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {Object.keys(preview.sample_data[0]).slice(0, 6).map(col => (
                          <TableHead key={col} className="text-xs">{col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.sample_data.slice(0, 10).map((row, idx) => (
                        <TableRow key={idx}>
                          {Object.entries(row).slice(0, 6).map(([key, val]) => (
                            <TableCell key={key} className="text-xs">
                              {String(val || "").substring(0, 30)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Mapping
              </Button>
              <Button 
                onClick={handleCommit} 
                disabled={loading || (preview.validation_errors?.length > preview.exceptions_count * 0.5)}
                data-testid="commit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Commit Import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === 4 && commitResult && (
        <Card data-testid="step-complete">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{commitResult.created_count || 0}</div>
                  <div className="text-sm text-slate-500">Records Created</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{commitResult.updated_count || 0}</div>
                  <div className="text-sm text-slate-500">Records Updated</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-slate-600">{commitResult.skipped_count || 0}</div>
                  <div className="text-sm text-slate-500">Skipped</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{commitResult.error_count || 0}</div>
                  <div className="text-sm text-slate-500">Errors</div>
                </CardContent>
              </Card>
            </div>

            {commitResult.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800">Import Errors</h4>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  {commitResult.errors.slice(0, 20).map((err, idx) => (
                    <div key={idx} className="text-sm text-red-700">
                      Row {err.row}: {err.field} - {err.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => {
                setCurrentStep(1);
                setSelectedFile(null);
                setUploadResult(null);
                setPreview(null);
                setCommitResult(null);
                setColumnMap({});
              }}>
                <Upload className="h-4 w-4 mr-2" /> Import Another File
              </Button>
              <Button onClick={() => navigate("/evv")} data-testid="go-to-inbox-btn">
                <ArrowRight className="h-4 w-4 mr-2" /> Go to EVV Inbox
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </Layout>
  );
};

export default SandataImportPage;
